<!-- src/lib/components/TableView.svelte -->
<script lang="ts">
  import { tick } from 'svelte';
  import { pmStore } from '../stores/pm.svelte.ts';
  import { api } from '../api.ts';
  import StatusIcon from './StatusIcon.svelte';
  import PrioritySignal from './PrioritySignal.svelte';
  import { shortDate, formatDueDate, dueUrgency, DUE_URGENCY_COLORS } from '../format-time.ts';

  interface Props {
    boardId: string;
    onOpenCard: (id: string) => void;
  }
  let { boardId, onOpenCard }: Props = $props();

  // ── Inline quick-edit (Round 9) ─────────────────────────────
  // Clicking a status or priority cell opens a small popover picker
  // anchored to the cell. The cell stops propagation so the row
  // click (onOpenCard) doesn't fire. PATCH goes to the server,
  // then we optimistically update the local store and close the
  // popover.

  type EditingCell = null | { kind: 'status'; cardId: string; x: number; y: number }
                          | { kind: 'priority'; cardId: string; x: number; y: number };
  let editingCell = $state<EditingCell>(null);
  type EditingTitle = null | { cardId: string; value: string; original: string; saving: boolean };
  let editingTitle = $state<EditingTitle>(null);
  let titleInput: HTMLInputElement | null = $state(null);
  let titleClickTimer: ReturnType<typeof setTimeout> | null = null;

  const PRIORITIES: Array<{ value: string; label: string }> = [
    { value: 'urgent', label: 'Urgent' },
    { value: 'high',   label: 'High'   },
    { value: 'medium', label: 'Medium' },
    { value: 'low',    label: 'Low'    },
    { value: 'none',   label: 'None'   },
  ];

  function openStatusPicker(e: MouseEvent, cardId: string) {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    editingCell = { kind: 'status', cardId, x: rect.left, y: rect.bottom + 4 };
  }

  function openPriorityPicker(e: MouseEvent, cardId: string) {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    editingCell = { kind: 'priority', cardId, x: rect.left, y: rect.bottom + 4 };
  }

  async function setStatus(cardId: string, columnId: string) {
    editingCell = null;
    // Optimistic: move card to the new column in local store
    if (detail) {
      for (const col of detail.columns) {
        const idx = col.cards.findIndex(c => c.id === cardId);
        if (idx !== -1) {
          const [card] = col.cards.splice(idx, 1);
          const target = detail.columns.find(c => c.id === columnId);
          if (target) {
            card.column_id = columnId;
            target.cards.push(card);
          }
          break;
        }
      }
    }
    await api(`/api/v1/cards/${cardId}`, {
      method: 'PATCH',
      body: JSON.stringify({ column_id: columnId }),
    }).catch(() => {});
  }

  async function setPriority(cardId: string, priority: string) {
    editingCell = null;
    // Optimistic: update local
    if (detail) {
      for (const col of detail.columns) {
        const card = col.cards.find(c => c.id === cardId);
        if (card) { card.priority = priority as any; break; }
      }
    }
    await api(`/api/v1/cards/${cardId}`, {
      method: 'PATCH',
      body: JSON.stringify({ priority }),
    }).catch(() => {});
  }

  function closePopover() {
    editingCell = null;
  }

  // sortKey: 'priority' | 'title' | 'status' | 'agent' | 'due_date' | 'created_at'
  // sortDir: 'asc' | 'desc'
  let sortKey = $state<string>('priority');
  let sortDir = $state<'asc' | 'desc'>('asc');
  let filterQuery = $state('');

  // ── Round 13: Grouping ──────────────────────────────────────────
  type GroupBy = 'none' | 'priority' | 'status';
  let groupBy = $state<GroupBy>('none');
  let collapsedGroups = $state<Set<string>>(new Set());
  let groupByOpen = $state(false);
  const groupByOptions = [
    { value: 'none',     label: 'No grouping' },
    { value: 'status',   label: 'Status'      },
    { value: 'priority', label: 'Priority'    },
  ];

  const PRIORITY_ORDER: Record<string, number> = {
    urgent: 0, high: 1, medium: 2, low: 3, none: 4
  };

  const detail = $derived(pmStore.boardDetails[boardId]);

  function findCard(cardId: string) {
    if (!detail) return null;
    for (const column of detail.columns) {
      const card = column.cards.find((item) => item.id === cardId);
      if (card) return card;
    }
    return null;
  }

  function handleTitleClick(event: MouseEvent, cardId: string) {
    event.stopPropagation();
    if (titleClickTimer) clearTimeout(titleClickTimer);
    titleClickTimer = setTimeout(() => {
      onOpenCard(cardId);
      titleClickTimer = null;
    }, 180);
  }

  async function beginTitleEdit(event: MouseEvent, row: { id: string; title: string }) {
    event.stopPropagation();
    if (titleClickTimer) {
      clearTimeout(titleClickTimer);
      titleClickTimer = null;
    }
    editingTitle = { cardId: row.id, value: row.title, original: row.title, saving: false };
    await tick();
    titleInput?.focus();
    titleInput?.select();
  }

  function cancelTitleEdit() {
    if (titleClickTimer) {
      clearTimeout(titleClickTimer);
      titleClickTimer = null;
    }
    editingTitle = null;
  }

  async function commitTitleEdit() {
    if (!editingTitle || editingTitle.saving) return;

    const snapshot = editingTitle;
    const nextTitle = snapshot.value.trim();
    if (!nextTitle || nextTitle === snapshot.original) {
      editingTitle = null;
      return;
    }

    const card = findCard(snapshot.cardId);
    if (!card) {
      editingTitle = null;
      return;
    }

    card.title = nextTitle;
    editingTitle = { ...snapshot, value: nextTitle, saving: true };

    try {
      await api(`/api/v1/cards/${snapshot.cardId}`, {
        method: 'PATCH',
        body: JSON.stringify({ title: nextTitle }),
      });
      editingTitle = null;
    } catch (error) {
      card.title = snapshot.original;
      console.warn('Failed to update card title:', error);
      editingTitle = { ...snapshot, value: snapshot.original, saving: false };
      await tick();
      titleInput?.focus();
      titleInput?.select();
    }
  }

  function handleTitleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      void commitTitleEdit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancelTitleEdit();
    }
  }

  // Build flat row list with column name resolved
  const allRows = $derived((() => {
    if (!detail) return [];
    return detail.columns.flatMap(col =>
      col.cards.map(card => ({
        ...card,
        columnName: col.name,
      }))
    );
  })());

  const filtered = $derived((() => {
    let rows = allRows;
    if (filterQuery.trim()) {
      const q = filterQuery.toLowerCase();
      rows = rows.filter(r => r.title.toLowerCase().includes(q));
    }
    // Sort
    rows = [...rows].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'priority') {
        cmp = (PRIORITY_ORDER[a.priority] ?? 4) - (PRIORITY_ORDER[b.priority] ?? 4);
      } else if (sortKey === 'title') {
        cmp = a.title.localeCompare(b.title);
      } else if (sortKey === 'status') {
        cmp = a.columnName.localeCompare(b.columnName);
      } else if (sortKey === 'agent') {
        cmp = (a.agent_id ?? '').localeCompare(b.agent_id ?? '');
      } else if (sortKey === 'due_date') {
        cmp = (a.due_date ?? '').localeCompare(b.due_date ?? '');
      } else if (sortKey === 'created_at') {
        cmp = a.created_at.localeCompare(b.created_at);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  })());

  interface RowGroup { key: string; label: string; rows: typeof allRows; }

  const grouped = $derived((() => {
    if (groupBy === 'none') return [{ key: '__all', label: '', rows: filtered }] as RowGroup[];
    const map = new Map<string, typeof allRows>();
    for (const row of filtered) {
      const k = groupBy === 'priority' ? row.priority : row.columnName;
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(row);
    }
    // Sort groups: priority by PRIORITY_ORDER, status alphabetically
    const entries = [...map.entries()];
    if (groupBy === 'priority') {
      entries.sort((a, b) => (PRIORITY_ORDER[a[0]] ?? 4) - (PRIORITY_ORDER[b[0]] ?? 4));
    } else {
      entries.sort((a, b) => a[0].localeCompare(b[0]));
    }
    return entries.map(([k, rows]) => ({
      key: k,
      label: k.charAt(0).toUpperCase() + k.slice(1),
      rows,
    })) as RowGroup[];
  })());

  function toggleCollapse(key: string) {
    const next = new Set(collapsedGroups);
    if (next.has(key)) next.delete(key); else next.add(key);
    collapsedGroups = next;
  }

  function toggleSort(key: string) {
    if (sortKey === key) {
      sortDir = sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      sortKey = key;
      sortDir = 'asc';
    }
  }

  function sortIndicator(key: string): string {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  }

  // ── Column visibility toggle ────────────────────────────────────
  const ALL_COLUMNS = [
    { key: 'priority',   label: 'Priority'   },
    { key: 'id',         label: 'ID'         },
    { key: 'title',      label: 'Title'      },
    { key: 'status',     label: 'Status'     },
    { key: 'assignee',   label: 'Assignee'   },
    { key: 'due_date',   label: 'Due'        },
    { key: 'start_date', label: 'Start Date' },
    { key: 'estimate',   label: 'Estimate'   },
    { key: 'source',     label: 'Source'     },
    { key: 'labels',     label: 'Labels'     },
  ] as const;

  type ColumnKey = (typeof ALL_COLUMNS)[number]['key'];

  let visibleColumns = $state(new Set<ColumnKey>([
    'priority', 'id', 'title', 'status', 'assignee',
    'due_date', 'start_date', 'estimate', 'source', 'labels',
  ]));

  let columnsMenuOpen = $state(false);

  function toggleColumn(key: ColumnKey) {
    const next = new Set(visibleColumns);
    if (next.has(key)) {
      // Don't allow hiding title — always need at least one clickable column
      if (key === 'title') return;
      next.delete(key);
    } else {
      next.add(key);
    }
    visibleColumns = next;
  }

  // Infer source name from a URL string (or null)
  function sourceName(url: string | null | undefined): string {
    if (!url) return '';
    if (url.includes('linear.app'))    return 'Linear';
    if (url.includes('plane.so'))      return 'Plane';
    if (url.includes('app.asana.com')) return 'Asana';
    if (url.includes('figma.com'))     return 'Figma';
    return 'External';
  }

  // Total visible column count (for colspan on empty/group rows)
  const colCount = $derived(visibleColumns.size);
</script>

<div class="table-container">
  <div class="table-toolbar">
    <input
      class="search-input"
      type="text"
      placeholder="Filter by title…"
      bind:value={filterQuery}
    />
    <span class="row-count">{filtered.length} card{filtered.length === 1 ? '' : 's'}</span>
    <div class="group-by-picker" class:open={groupByOpen}>
      <button
        class="group-by-btn"
        onclick={() => { groupByOpen = !groupByOpen; }}
      >
        Group: {groupByOptions.find(o => o.value === groupBy)?.label ?? 'None'}
        <span class="chevron" class:open={groupByOpen}>⌄</span>
      </button>
      {#if groupByOpen}
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <div
          class="group-by-overlay"
          role="presentation"
          onclick={() => { groupByOpen = false; }}
        ></div>
        <ul class="group-by-menu">
          {#each groupByOptions as opt}
            <li>
              <button
                class="group-by-item"
                class:active={groupBy === opt.value}
                onclick={() => { groupBy = opt.value as typeof groupBy; groupByOpen = false; }}
              >{opt.label}</button>
            </li>
          {/each}
        </ul>
      {/if}
    </div>

    <!-- Column visibility toggle -->
    <div class="col-toggle-wrap">
      <!-- svelte-ignore a11y_consider_explicit_label -->
      <button
        class="col-toggle-btn"
        class:active={columnsMenuOpen}
        onclick={() => { columnsMenuOpen = !columnsMenuOpen; }}
      >
        Columns ▾
      </button>
      {#if columnsMenuOpen}
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <div class="col-menu-backdrop" onclick={() => { columnsMenuOpen = false; }}></div>
        <div class="col-menu">
          {#each ALL_COLUMNS as col (col.key)}
            <label class="col-menu-item">
              <input
                type="checkbox"
                checked={visibleColumns.has(col.key)}
                disabled={col.key === 'title'}
                onchange={() => toggleColumn(col.key)}
              />
              <span>{col.label}</span>
            </label>
          {/each}
        </div>
      {/if}
    </div>
  </div>

  <div class="table-wrap">
    <table>
      <thead>
        <tr>
          {#if visibleColumns.has('priority')}
            <th class="col-priority" class:sorted={sortKey === 'priority'} onclick={() => toggleSort('priority')}>
              Priority{sortIndicator('priority')}
            </th>
          {/if}
          {#if visibleColumns.has('id')}
            <th class="col-id">ID</th>
          {/if}
          {#if visibleColumns.has('title')}
            <th class="col-title" class:sorted={sortKey === 'title'} onclick={() => toggleSort('title')}>
              Title{sortIndicator('title')}
            </th>
          {/if}
          {#if visibleColumns.has('status')}
            <th class="col-status" class:sorted={sortKey === 'status'} onclick={() => toggleSort('status')}>
              Status{sortIndicator('status')}
            </th>
          {/if}
          {#if visibleColumns.has('assignee')}
            <th class="col-assignee">Assignee</th>
          {/if}
          {#if visibleColumns.has('due_date')}
            <th class="col-due" class:sorted={sortKey === 'due_date'} onclick={() => toggleSort('due_date')}>
              Due{sortIndicator('due_date')}
            </th>
          {/if}
          {#if visibleColumns.has('start_date')}
            <th class="col-start">Start Date</th>
          {/if}
          {#if visibleColumns.has('estimate')}
            <th class="col-estimate">Estimate</th>
          {/if}
          {#if visibleColumns.has('source')}
            <th class="col-source">Source</th>
          {/if}
          {#if visibleColumns.has('labels')}
            <th class="col-labels">Labels</th>
          {/if}
        </tr>
      </thead>
      <tbody>
        {#if !pmStore.activeProjectId}
          <tr class="empty-row">
            <td colspan={colCount}>
              <div class="empty-cta">
                <span class="empty-icon">≡</span>
                <span class="empty-heading">No project selected</span>
                <span class="empty-sub">Pick a project from the sidebar to view its cards as a table.</span>
              </div>
            </td>
          </tr>
        {:else if !detail}
          <tr class="empty-row">
            <td colspan={colCount}>
              <div class="empty-cta">
                <span class="empty-icon empty-spin">◌</span>
                <span class="empty-heading">Loading…</span>
              </div>
            </td>
          </tr>
        {:else if filtered.length === 0}
          <tr class="empty-row">
            <td colspan={colCount}>
              <div class="empty-cta">
                <span class="empty-icon">◻</span>
                <span class="empty-heading">{filterQuery.trim() ? 'No cards match' : 'No cards yet'}</span>
                {#if !filterQuery.trim()}
                  <span class="empty-sub">Create cards from the Board view or use ⌘K → New task.</span>
                {/if}
              </div>
            </td>
          </tr>
        {:else}
          {#each grouped as group (group.key)}
            {#if group.label}
              <!-- svelte-ignore a11y_click_events_have_key_events -->
              <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
              <tr class="group-header" onclick={() => toggleCollapse(group.key)}>
                <td colspan={colCount}>
                  <span class="group-chevron" class:collapsed={collapsedGroups.has(group.key)}>▾</span>
                  <span class="group-label">{group.label}</span>
                  <span class="group-count">{group.rows.length}</span>
                </td>
              </tr>
            {/if}
            {#if !collapsedGroups.has(group.key)}
              {#each group.rows as row, i}
                <!-- svelte-ignore a11y_click_events_have_key_events -->
                <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
                <tr
                  class="data-row"
                  class:alt={i % 2 === 1}
                  onclick={() => onOpenCard(row.id)}
                >
                  {#if visibleColumns.has('priority')}
                    <!-- svelte-ignore a11y_click_events_have_key_events -->
                    <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
                    <td class="col-priority editable" onclick={(e) => openPriorityPicker(e, row.id)} title="Click to change priority">
                      <PrioritySignal priority={row.priority} size={14} />
                    </td>
                  {/if}
                  {#if visibleColumns.has('id')}
                    <td class="col-id">
                      <span class="id-chip">{row.human_id ?? '#' + row.id.slice(-4)}</span>
                    </td>
                  {/if}
                  {#if visibleColumns.has('title')}
                    <td
                      class="col-title"
                      class:editing={editingTitle?.cardId === row.id}
                      onclick={(event) => handleTitleClick(event, row.id)}
                      ondblclick={(event) => { void beginTitleEdit(event, row); }}
                    >
                      {#if editingTitle?.cardId === row.id}
                        <input
                          bind:this={titleInput}
                          class="title-edit-input"
                          value={editingTitle.value}
                          oninput={(event) => {
                            if (!editingTitle) return;
                            editingTitle = {
                              ...editingTitle,
                              value: (event.target as HTMLInputElement).value,
                            };
                          }}
                          onkeydown={handleTitleKeydown}
                          onblur={() => { void commitTitleEdit(); }}
                          onclick={(event) => { event.stopPropagation(); }}
                        />
                      {:else}
                        <span class="title-text" title={row.title}>{row.title}</span>
                      {/if}
                    </td>
                  {/if}
                  {#if visibleColumns.has('status')}
                    <!-- svelte-ignore a11y_click_events_have_key_events -->
                    <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
                    <td class="col-status editable" onclick={(e) => openStatusPicker(e, row.id)} title="Click to change status">
                      <span class="status-cell">
                        <StatusIcon status={row.columnName} size={12} />
                        <span class="status-label">{row.columnName}</span>
                      </span>
                    </td>
                  {/if}
                  {#if visibleColumns.has('assignee')}
                    <td class="col-assignee">
                      {#if row.assignee_id}
                        <span class="assignee-chip" title="{row.assignee_kind ?? ''}: {row.assignee_id}">
                          {row.assignee_id.length > 12 ? row.assignee_id.slice(0, 12) + '…' : row.assignee_id}
                        </span>
                      {:else}
                        <span class="muted">—</span>
                      {/if}
                    </td>
                  {/if}
                  {#if visibleColumns.has('due_date')}
                    <td class="col-due">
                      {#if row.due_date}
                        <span class="due-chip" style="color: {DUE_URGENCY_COLORS[dueUrgency(row.due_date)]}">
                          {formatDueDate(row.due_date)}
                        </span>
                      {:else}
                        <span class="muted">—</span>
                      {/if}
                    </td>
                  {/if}
                  {#if visibleColumns.has('start_date')}
                    <td class="col-start">
                      {#if row.start_date}
                        <span>{shortDate(row.start_date)}</span>
                      {:else}
                        <span class="muted">—</span>
                      {/if}
                    </td>
                  {/if}
                  {#if visibleColumns.has('estimate')}
                    <td class="col-estimate">
                      {#if row.estimate != null}
                        <span class="estimate-chip">{row.estimate} pts</span>
                      {:else}
                        <span class="muted">—</span>
                      {/if}
                    </td>
                  {/if}
                  {#if visibleColumns.has('source')}
                    <td class="col-source">
                      {#if row.source_url}
                        <a
                          class="source-badge"
                          href={row.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onclick={(e) => e.stopPropagation()}
                        >{sourceName(row.source_url)}</a>
                      {:else}
                        <span class="muted">—</span>
                      {/if}
                    </td>
                  {/if}
                  {#if visibleColumns.has('labels')}
                    <td class="col-labels">
                      <span class="muted">—</span>
                    </td>
                  {/if}
                </tr>
              {/each}
            {/if}
          {/each}
        {/if}
      </tbody>
    </table>
  </div>

  <!-- Inline edit popover — anchored to the clicked cell via fixed position -->
  {#if editingCell}
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div class="popover-backdrop" onclick={closePopover}></div>
    <div
      class="popover"
      style="left: {editingCell.x}px; top: {editingCell.y}px;"
    >
      {#if editingCell.kind === 'status' && detail}
        {#each detail.columns as col (col.id)}
          <button class="popover-item" onclick={() => setStatus(editingCell?.cardId ?? '', col.id)}>
            <StatusIcon status={col.name} size={13} />
            <span>{col.name}</span>
          </button>
        {/each}
      {:else if editingCell.kind === 'priority'}
        {#each PRIORITIES as p (p.value)}
          <button class="popover-item" onclick={() => setPriority(editingCell?.cardId ?? '', p.value)}>
            <PrioritySignal priority={p.value} size={13} />
            <span>{p.label}</span>
          </button>
        {/each}
      {/if}
    </div>
  {/if}
</div>

<style>
  .table-container {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    background: var(--bg-base);
  }

  /* ── Toolbar ─────────────────────────────────────── */
  .table-toolbar {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.625rem 1rem;
    border-bottom: 1px solid var(--border-faint, rgba(255,255,255,0.06));
    flex-shrink: 0;
    background: var(--bg-elevated, oklch(17% 0.015 270));
  }

  .search-input {
    background: var(--bg-base, oklch(14% 0.01 270));
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 5px;
    color: var(--text-primary, oklch(92% 0.01 270));
    font-size: 0.78rem;
    padding: 0.3rem 0.625rem;
    outline: none;
    width: 220px;
    font-family: inherit;
    transition: border-color 120ms ease;
  }

  .search-input::placeholder {
    color: var(--text-muted, oklch(55% 0.01 270));
  }

  .search-input:focus {
    border-color: oklch(0.74 0.28 310 / 0.39);
  }

  .row-count {
    font-size: 0.72rem;
    color: var(--text-muted, oklch(55% 0.01 270));
    font-variant-numeric: tabular-nums;
  }

  /* ── Table wrapper ───────────────────────────────── */
  .table-wrap {
    flex: 1;
    overflow-y: auto;
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.8rem;
    table-layout: fixed;
  }

  /* ── Header ──────────────────────────────────────── */
  thead tr {
    background: var(--bg-elevated, oklch(17% 0.015 270));
    position: sticky;
    top: 0;
    z-index: 2;
  }

  thead th {
    text-align: left;
    padding: 0 0.75rem;
    height: 2.25rem;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-muted, oklch(55% 0.01 270));
    border-bottom: 1px solid var(--border-faint, rgba(255,255,255,0.06));
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
    transition: color 100ms ease;
  }

  thead th:hover {
    color: var(--text-primary, oklch(92% 0.01 270));
  }

  thead th.sorted {
    color: var(--accent-green, oklch(80% 0.2 145));
  }

  /* ── Column widths ───────────────────────────────── */
  .col-priority  { width: 72px; }
  .col-id        { width: 80px; }
  .col-title     { width: auto; min-width: 200px; }
  .col-status    { width: 140px; }
  .col-assignee  { width: 110px; }
  .col-due       { width: 90px; }
  .col-start     { width: 90px; }
  .col-estimate  { width: 80px; }
  .col-source    { width: 80px; }
  .col-labels    { width: 120px; }

  /* ── ID chip — Linear-style gold monospace pill ─── */
  .id-chip {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.68rem;
    color: oklch(0.78 0.16 85 / 0.9);
    background: oklch(0.78 0.16 85 / 0.08);
    border: 1px solid oklch(0.78 0.16 85 / 0.25);
    padding: 0.08rem 0.35rem;
    border-radius: 3px;
    letter-spacing: 0.02em;
  }

  /* ── Body rows ───────────────────────────────────── */
  tbody tr.data-row {
    height: 2.25rem;
    border-bottom: 1px solid var(--border-faint, rgba(255,255,255,0.06));
    cursor: pointer;
    transition: background 80ms ease;
  }

  tbody tr.data-row:hover {
    background: var(--bg-surface, oklch(20% 0.02 270));
    filter: brightness(1.08);
  }

  tbody tr.data-row.alt {
    background: var(--bg-surface, oklch(20% 0.02 270));
  }

  tbody td {
    padding: 0 0.75rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--text-primary, oklch(92% 0.01 270));
  }

  /* ── Title ───────────────────────────────────────── */
  .title-text {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--text-primary, oklch(92% 0.01 270));
  }
  .col-title.editing {
    padding: 0.2rem 0.55rem;
  }
  .title-edit-input {
    width: 100%;
    background: oklch(0.14 0.02 275);
    border: 1px solid oklch(0.72 0.28 310 / 0.35);
    border-radius: 5px;
    color: var(--text-primary, oklch(92% 0.01 270));
    font-size: 0.76rem;
    padding: 0.35rem 0.45rem;
    outline: none;
  }
  .title-edit-input:focus {
    border-color: oklch(0.72 0.28 310 / 0.50);
    box-shadow: 0 0 0 1px oklch(0.72 0.28 310 / 0.16);
  }

  /* ── Status cell — icon + label, matches the board glyph set ─── */
  .status-cell {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
  }
  .status-label {
    font-size: 0.7rem;
    color: var(--text-muted, oklch(55% 0.01 270));
  }

  /* ── Due-date urgency chip ────────────────────────── */
  .due-chip {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.7rem;
    letter-spacing: 0.02em;
  }

  /* ── Muted ───────────────────────────────────────── */
  .muted {
    color: var(--text-muted, oklch(55% 0.01 270));
  }

  /* ── Empty state ─────────────────────────────────── */
  .empty-row td {
    text-align: center;
    padding: 3rem 1rem;
    color: var(--text-muted, oklch(55% 0.01 270));
    font-size: 0.85rem;
  }

  /* ── Round 13: Grouping ──────────────────────────── */
  .group-header td {
    padding: 0.55rem 0.75rem;
    background: oklch(0.13 0.02 275);
    border-bottom: 1px solid oklch(0.30 0.05 275 / 0.25);
    cursor: pointer;
    user-select: none;
  }
  .group-header td:hover {
    background: oklch(0.16 0.03 275);
  }
  .group-chevron {
    display: inline-block;
    transition: transform 120ms ease;
    margin-right: 0.4rem;
    font-size: 0.7rem;
    color: oklch(0.60 0.05 310 / 0.6);
  }
  .group-chevron.collapsed {
    transform: rotate(-90deg);
  }
  .group-label {
    font-size: 0.78rem;
    font-weight: 600;
    color: oklch(0.88 0.06 310);
    text-transform: capitalize;
  }
  .group-count {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.65rem;
    color: oklch(0.55 0.04 310 / 0.55);
    margin-left: 0.5rem;
    background: oklch(0.22 0.03 275 / 0.5);
    padding: 0.08rem 0.35rem;
    border-radius: 8px;
  }

  /* ── Round 9: Inline quick-edit ─────────────────── */
  .editable {
    cursor: pointer;
    border-radius: 3px;
    transition: background 100ms ease;
  }
  .editable:hover {
    background: oklch(0.72 0.28 310 / 0.08);
  }

  .popover-backdrop {
    position: fixed;
    inset: 0;
    z-index: 60;
  }
  .popover {
    position: fixed;
    z-index: 61;
    min-width: 160px;
    background: oklch(0.095 0.018 275);
    border: 1px solid oklch(0.35 0.06 275 / 0.45);
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.40);
    padding: 0.3rem;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }
  .popover-item {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    width: 100%;
    background: transparent;
    border: none;
    color: oklch(0.88 0.05 310);
    font-size: 0.78rem;
    padding: 0.4rem 0.6rem;
    border-radius: 4px;
    cursor: pointer;
    text-align: left;
    transition: background 100ms ease;
  }
  .popover-item:hover {
    background: oklch(0.22 0.08 310 / 0.35);
    color: oklch(0.96 0.08 310);
  }

  /* ── Column visibility toggle ────────────────────── */
  .col-toggle-wrap {
    position: relative;
  }
  .col-toggle-btn {
    font-size: 0.72rem;
    padding: 0.3rem 0.625rem;
    background: var(--bg-base, oklch(14% 0.01 270));
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 5px;
    color: var(--text-muted, oklch(55% 0.01 270));
    cursor: pointer;
    font-family: inherit;
    transition: border-color 100ms ease, color 100ms ease;
    white-space: nowrap;
  }
  .col-toggle-btn:hover,
  .col-toggle-btn.active {
    border-color: oklch(0.74 0.28 310 / 0.39);
    color: var(--text-primary, oklch(92% 0.01 270));
  }
  .col-menu-backdrop {
    position: fixed;
    inset: 0;
    z-index: 60;
  }
  .col-menu {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    z-index: 61;
    min-width: 140px;
    background: oklch(0.095 0.018 275);
    border: 1px solid oklch(0.35 0.06 275 / 0.45);
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.40);
    padding: 0.35rem;
    display: flex;
    flex-direction: column;
    gap: 0.05rem;
  }
  .col-menu-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.35rem 0.55rem;
    border-radius: 4px;
    font-size: 0.76rem;
    color: oklch(0.88 0.05 310);
    cursor: pointer;
    user-select: none;
    transition: background 80ms ease;
  }
  .col-menu-item:hover {
    background: oklch(0.22 0.08 310 / 0.30);
  }
  .col-menu-item input[type="checkbox"] {
    accent-color: oklch(0.78 0.26 310);
    cursor: pointer;
  }
  .col-menu-item input[type="checkbox"]:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* ── New column cells ────────────────────────────── */
  .assignee-chip {
    font-size: 0.72rem;
    color: oklch(0.78 0.10 265 / 0.85);
    background: oklch(0.55 0.20 265 / 0.08);
    border: 1px solid oklch(0.55 0.20 265 / 0.22);
    padding: 0.06rem 0.35rem;
    border-radius: 3px;
  }
  .estimate-chip {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.7rem;
    color: oklch(0.78 0.16 85 / 0.80);
  }
  .source-badge {
    font-size: 0.68rem;
    font-weight: 600;
    padding: 0.06rem 0.35rem;
    border-radius: 3px;
    background: oklch(0.55 0.20 265 / 0.10);
    border: 1px solid oklch(0.55 0.20 265 / 0.25);
    color: oklch(0.70 0.18 265 / 0.90);
    text-decoration: none;
    letter-spacing: 0.02em;
    transition: background 100ms ease, color 100ms ease;
  }
  .source-badge:hover {
    background: oklch(0.55 0.20 265 / 0.18);
    color: oklch(0.80 0.20 265);
  }

  /* ── Styled group-by dropdown (#27) ─────────────── */
  .group-by-picker {
    position: relative;
    margin-left: auto;
  }
  .group-by-btn {
    display: flex; align-items: center; gap: 0.4rem;
    background: oklch(0.14 0.02 275 / 0.6);
    border: 1px solid oklch(0.30 0.05 275 / 0.25);
    border-radius: 6px; color: oklch(0.85 0.06 310);
    font-size: 0.78rem; padding: 0.35rem 0.65rem;
    cursor: pointer; white-space: nowrap;
    font-family: inherit;
    transition: background 100ms, border-color 100ms;
  }
  .group-by-btn:hover {
    background: oklch(0.18 0.03 275);
    border-color: oklch(0.72 0.28 310 / 0.3);
  }
  .group-by-btn .chevron {
    color: oklch(0.55 0.04 310 / 0.6);
    transition: transform 140ms ease;
    line-height: 1;
  }
  .group-by-btn .chevron.open { transform: rotate(180deg); }
  .group-by-overlay {
    position: fixed; inset: 0; z-index: 99;
  }
  .group-by-menu {
    position: absolute; top: calc(100% + 4px); left: 0;
    background: oklch(0.14 0.02 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.35);
    border-radius: 6px; z-index: 100;
    list-style: none; margin: 0; padding: 0.25rem;
    min-width: 140px;
    box-shadow: 0 4px 16px oklch(0 0 0 / 0.4);
  }
  .group-by-item {
    display: block; width: 100%;
    background: transparent; border: none;
    color: oklch(0.80 0.05 310); font-size: 0.80rem;
    text-align: left; padding: 0.4rem 0.6rem;
    border-radius: 4px; cursor: pointer;
    font-family: inherit;
    transition: background 100ms;
  }
  .group-by-item:hover { background: oklch(0.72 0.28 310 / 0.10); }
  .group-by-item.active {
    background: oklch(0.72 0.28 310 / 0.18);
    color: oklch(0.96 0.12 310);
  }

  /* ── Improved empty state (#28) ─────────────────── */
  .empty-cta {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.45rem;
    padding: 2rem 1rem;
  }
  .empty-icon {
    font-size: 1.8rem;
    color: oklch(0.45 0.04 275 / 0.6);
    line-height: 1;
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  .empty-spin {
    display: inline-block;
    animation: spin 1.2s linear infinite;
  }
  .empty-heading {
    font-size: 0.9rem;
    font-weight: 600;
    color: oklch(0.72 0.05 310);
  }
  .empty-sub {
    font-size: 0.78rem;
    color: oklch(0.55 0.03 275);
    text-align: center;
    max-width: 280px;
    line-height: 1.5;
  }
</style>
